/**
 * SIGNATURE SCREEN
 *
 * Real signature capture for seller (pickup) and buyer (delivery) confirmation.
 * Uses react-native-signature-canvas on native (iOS/Android) with a WebView-based
 * drawing pad. Falls back to a typed-name input on Expo web where WebView is unavailable.
 */
import React, { useRef, useState } from 'react';
import {
  View, Text, StyleSheet, TouchableOpacity, Alert,
  ActivityIndicator, Platform, TextInput, KeyboardAvoidingView,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { spacing, radii, RD } from '../../theme/colors';
import { api } from '../../services/api';

/* ---------- conditional import for native signature pad ---------- */
let SignatureCanvas: any = null;
if (Platform.OS !== 'web') {
  // react-native-signature-canvas relies on react-native-webview which
  // does not work on Expo web — only import on native platforms.
  try {
    SignatureCanvas = require('react-native-signature-canvas').default;
  } catch {
    // Will fall through to the web fallback UI
  }
}

/* ================================================================= */

export function SignatureScreen({ route, navigation }: any) {
  const { jobId, type } = route.params; // type: 'PICKUP' | 'DELIVERY'
  const party = type === 'PICKUP' ? 'Seller' : 'Buyer';

  const sigRef = useRef<any>(null);
  const [signatureBase64, setSignatureBase64] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  // Web fallback state
  const [typedName, setTypedName] = useState('');

  // Draw is only available on native with the canvas lib present; otherwise
  // we fall back to typed-name. The toggle lets the user switch on native.
  const canDraw = Platform.OS !== 'web' && !!SignatureCanvas;
  const [mode, setMode] = useState<'draw' | 'type'>(canDraw ? 'draw' : 'type');

  const hasSigned = mode === 'type'
    ? typedName.trim().length >= 3
    : signatureBase64 !== null;

  /* ---- native signature handlers ---- */
  const handleSignatureEnd = () => {
    // Called when the user lifts their finger — read the current image
    sigRef.current?.readSignature();
  };

  const handleOK = (base64: string) => {
    // react-native-signature-canvas returns a data URI string
    setSignatureBase64(base64);
  };

  const handleClear = () => {
    sigRef.current?.clearSignature();
    setSignatureBase64(null);
    setTypedName('');
  };

  /* ---- submit ---- */
  const handleSubmit = async () => {
    if (!hasSigned) {
      Alert.alert('Signature Required', 'Please provide a signature before submitting.');
      return;
    }

    // No re-read here: handleSignatureEnd (onEnd / finger-lift) already pushed the latest
    // stroke into signatureBase64, and `hasSigned` requires that to be set. A synchronous
    // readSignature() at submit time resolves AFTER this function builds the payload, so it
    // would race — using the just-read value below would actually send the PRE-read state.
    setSubmitting(true);
    const payload =
      mode === 'type'
        ? { type, signatureData: typedName.trim(), signatureType: 'typed' }
        : { type, signatureData: signatureBase64, signatureType: 'drawn' };
    try {
      await api.post(`/jobs/${jobId}/signature`, payload);
    } catch (err: any) {
      // A signature is a proof artifact — do NOT mark the step complete if it didn't save.
      // Keep the driver on this screen to retry instead of silently advancing.
      setSubmitting(false);
      Alert.alert('Could not save signature', 'Please check your connection and tap Submit again.');
      return;
    }
    setSubmitting(false);
    // Signal completion back to ActiveJobScreen so it can advance status
    navigation.navigate('ActiveJob', {
      completedAction: type === 'PICKUP' ? 'SELLER_SIGNATURE_COMPLETE' : 'BUYER_SIGNATURE_COMPLETE',
      jobId,
    });
  };

  /* ---- style injected into the signature WebView ---- */
  const webStyle = `.m-signature-pad--footer { display: none; }
    .m-signature-pad { box-shadow: none; border: none; }
    .m-signature-pad--body { border: none; }
    body, html { background: #FFFFFF; }`;

  /* ================================================================= */

  return (
    <View style={styles.container}>
      {/* Header */}
      <View style={styles.header}>
        <TouchableOpacity onPress={() => navigation.goBack()} style={styles.backBtn} hitSlop={{ top: 12, bottom: 12, left: 12, right: 12 }} activeOpacity={0.7}>
          <Ionicons name="chevron-back" size={24} color={RD.text} />
        </TouchableOpacity>
        <View style={styles.headerCenter}>
          <Text style={styles.headerTitle}>{party} Signature</Text>
          <Text style={styles.headerSub}>Confirm {type === 'PICKUP' ? 'pickup' : 'delivery'} is complete</Text>
        </View>
        <View style={{ width: 22 }} />
      </View>

      <KeyboardAvoidingView
        style={styles.flex}
        behavior={Platform.OS === 'ios' ? 'padding' : undefined}
      >
      <View style={styles.content}>
        {/* Draw / Type-name segmented toggle */}
        <View style={styles.segment}>
          {([['draw', 'create-outline', 'Draw'], ['type', 'text-outline', 'Type name']] as const).map(
            ([m, icon, label]) => {
              const on = mode === m;
              const disabled = m === 'draw' && !canDraw;
              return (
                <TouchableOpacity
                  key={m}
                  onPress={() => !disabled && setMode(m)}
                  disabled={disabled}
                  activeOpacity={0.8}
                  style={[styles.segmentBtn, on && styles.segmentBtnOn, disabled && styles.segmentBtnDisabled]}
                >
                  <Ionicons name={icon as any} size={16} color={on ? '#fff' : RD.g40} />
                  <Text style={[styles.segmentText, on && styles.segmentTextOn]}>{label}</Text>
                </TouchableOpacity>
              );
            }
          )}
        </View>

        {/* Pad — draw or typed-name */}
        <View style={styles.padCard}>
          {mode === 'draw' && canDraw ? (
            <View style={styles.padDraw}>
              <SignatureCanvas
                ref={sigRef}
                onEnd={handleSignatureEnd}
                onOK={handleOK}
                onEmpty={() => setSignatureBase64(null)}
                webStyle={webStyle}
                backgroundColor="#FFFFFF"
                penColor="#000000"
                minWidth={1.5}
                maxWidth={3}
                dotSize={2}
                style={styles.sigCanvas}
              />
            </View>
          ) : (
            <View style={styles.padType}>
              <Text style={styles.typeHint}>
                Can't sign on screen? Type the {party.toLowerCase()}'s full name to confirm.
              </Text>
              <Text style={styles.typeLabel}>{party}'s full name</Text>
              <TextInput
                style={styles.typeInput}
                placeholder={`e.g. ${party === 'Seller' ? 'Maria Garcia' : 'Justin Lee'}`}
                placeholderTextColor={RD.g30}
                value={typedName}
                onChangeText={setTypedName}
                autoCapitalize="words"
              />
              <View style={styles.typePreview}>
                <Text style={[styles.typePreviewText, !typedName && styles.typePreviewPlaceholder]}>
                  {typedName || `${party}'s name`}
                </Text>
              </View>
              {typedName.trim().length > 0 && typedName.trim().length < 3 && (
                <Text style={styles.typeWarn}>Name must be at least 3 characters.</Text>
              )}
            </View>
          )}
          <Text style={styles.padCaption}>
            {mode === 'draw'
              ? `Have the ${party.toLowerCase()} sign above`
              : `This typed name is recorded as the ${party.toLowerCase()}'s confirmation`}
          </Text>
        </View>
      </View>

      {/* Footer — Clear + Confirm */}
      <View style={styles.footer}>
        <TouchableOpacity style={styles.clearBtn} onPress={handleClear} activeOpacity={0.8}>
          <Ionicons name="refresh-outline" size={16} color={RD.textSec} />
          <Text style={styles.clearText}>Clear</Text>
        </TouchableOpacity>
        <TouchableOpacity
          style={[styles.submitBtn, (!hasSigned || submitting) && styles.btnDisabled]}
          onPress={handleSubmit}
          disabled={!hasSigned || submitting}
          activeOpacity={0.85}
        >
          {submitting ? (
            <ActivityIndicator color="#fff" />
          ) : (
            <Text style={styles.submitText}>Confirm Signature</Text>
          )}
        </TouchableOpacity>
      </View>
      </KeyboardAvoidingView>
    </View>
  );
}

/* ================================================================= */

const styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: RD.bg },
  flex: { flex: 1 },

  // Header
  header: {
    flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between',
    paddingHorizontal: spacing.lg,
    paddingTop: Platform.OS === 'ios' ? 56 : 36,
    paddingBottom: spacing.md,
    backgroundColor: RD.surface,
    borderBottomWidth: 1, borderBottomColor: RD.border,
  },
  backBtn: { padding: 4, minWidth: 44, minHeight: 44, justifyContent: 'center', alignItems: 'center' },
  headerCenter: { flex: 1, alignItems: 'center' },
  headerTitle: { fontSize: 17, fontWeight: '700', color: RD.text },
  headerSub: { fontSize: 12, color: RD.textFaint, marginTop: 2 },

  content: { flex: 1, padding: spacing.lg, gap: spacing.base },

  // Segmented toggle
  segment: {
    flexDirection: 'row', gap: 4, padding: 4,
    backgroundColor: RD.g05, borderRadius: 13,
  },
  segmentBtn: {
    flex: 1, height: 44, borderRadius: 10,
    flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 7,
    backgroundColor: 'transparent',
  },
  segmentBtnOn: { backgroundColor: RD.ink },
  segmentBtnDisabled: { opacity: 0.4 },
  segmentText: { fontSize: 13.5, fontWeight: '600', color: RD.textFaint },
  segmentTextOn: { color: '#fff' },

  // Pad card
  padCard: {
    flex: 1, minHeight: 320,
    backgroundColor: RD.surface, borderRadius: 18,
    borderWidth: 1, borderColor: RD.border, padding: spacing.base,
    shadowColor: '#000', shadowOpacity: 0.03, shadowRadius: 8, elevation: 1,
  },
  padDraw: {
    flex: 1,
    borderWidth: 1.5, borderColor: RD.g15, borderStyle: 'dashed',
    borderRadius: 14, backgroundColor: RD.g03, overflow: 'hidden',
  },
  sigCanvas: { flex: 1 },

  // Typed-name pad
  padType: { flex: 1, justifyContent: 'center', gap: 14 },
  typeHint: { fontSize: 12.5, color: RD.textFaint, textAlign: 'center' },
  typeLabel: { fontSize: 12, fontWeight: '700', color: RD.textSec, marginBottom: -6 },
  typeInput: {
    borderWidth: 1.5, borderColor: RD.borderStrong, borderRadius: radii.md,
    backgroundColor: RD.g03, paddingHorizontal: 14, height: 50,
    fontSize: 16, color: RD.text,
  },
  typePreview: {
    borderWidth: 1.5, borderColor: RD.g15, borderStyle: 'dashed',
    borderRadius: 14, padding: 18, alignItems: 'center', backgroundColor: RD.g03,
  },
  typePreviewText: { fontSize: 26, fontStyle: 'italic', color: RD.text },
  typePreviewPlaceholder: { color: RD.g30 },
  typeWarn: { fontSize: 11.5, color: RD.amber, textAlign: 'center' },

  padCaption: {
    textAlign: 'center', fontSize: 12, color: RD.textFaint, marginTop: 10,
  },

  // Footer — Clear + Confirm
  footer: {
    flexDirection: 'row', gap: 9,
    backgroundColor: RD.surface, padding: spacing.lg,
    paddingBottom: Platform.OS === 'ios' ? 36 : 20,
    borderTopWidth: 1, borderTopColor: RD.border,
  },
  clearBtn: {
    flex: 1, height: 54, borderRadius: 16,
    flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 6,
    backgroundColor: RD.g05, borderWidth: 1, borderColor: RD.borderStrong,
  },
  clearText: { color: RD.textSec, fontSize: 15, fontWeight: '600' },
  submitBtn: {
    flex: 2, backgroundColor: RD.ink, borderRadius: 16, height: 54,
    flexDirection: 'row', justifyContent: 'center', alignItems: 'center', gap: 8,
  },
  submitText: { color: '#fff', fontWeight: '700', fontSize: 17 },
  btnDisabled: { opacity: 0.4 },
});
